import { wsUrl } from './ws-url.js';

type WsHandler = (data: { type?: string; payload?: unknown; timestamp?: string }) => void;
type StatusHandler = (status: 'connecting' | 'open' | 'closed' | 'failed') => void;

const MAX_FAILURES = 10;

class WebSocketManager {
  private connections = new Map<string, WebSocket>();
  private listeners = new Map<string, Set<WsHandler>>();
  private reconnectTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private attempts = new Map<string, number>();
  private failures = new Map<string, number>();
  private paths = new Map<string, string>();
  private statusListeners = new Set<StatusHandler>();
  private channelStatus = new Map<string, 'connecting' | 'open' | 'closed' | 'failed'>();

  onStatus(handler: StatusHandler): () => void {
    this.statusListeners.add(handler);
    return () => this.statusListeners.delete(handler);
  }

  /** True when any managed channel has permanently failed reconnect. */
  hasFailedChannel(): boolean {
    for (const status of this.channelStatus.values()) {
      if (status === 'failed') return true;
    }
    return false;
  }

  private setStatus(channel: string, status: 'connecting' | 'open' | 'closed' | 'failed') {
    this.channelStatus.set(channel, status);
    this.statusListeners.forEach((fn) => fn(status));
  }

  connect(channel: string, path: string): void {
    this.paths.set(channel, path);

    if (this.channelStatus.get(channel) === 'failed') {
      return;
    }

    if (this.connections.has(channel)) {
      const existing = this.connections.get(channel);
      if (
        existing &&
        (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }
    }

    this.setStatus(channel, 'connecting');
    const url = wsUrl(path);
    const existing = this.connections.get(channel);
    if (existing) {
      existing.onclose = null;
      existing.onerror = null;
      existing.onmessage = null;
      existing.onopen = null;
      try {
        existing.close();
      } catch {
        /* ignore */
      }
      this.connections.delete(channel);
    }

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      this.scheduleReconnect(channel);
      return;
    }
    this.connections.set(channel, ws);

    ws.onopen = () => {
      this.attempts.set(channel, 0);
      this.failures.set(channel, 0);
      this.setStatus(channel, 'open');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data as string) as {
          type?: string;
          payload?: unknown;
          timestamp?: string;
        };
        if (data.type === 'ping') return;
        this.emit(channel, data);
      } catch {
        /* ignore */
      }
    };

    ws.onclose = () => {
      this.connections.delete(channel);
      this.scheduleReconnect(channel);
    };

    ws.onerror = () => {
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    };
  }

  private scheduleReconnect(channel: string): void {
    const path = this.paths.get(channel);
    if (!path) return;

    const listeners = this.listeners.get(channel);
    if (!listeners || listeners.size === 0) return;

    const failures = (this.failures.get(channel) ?? 0) + 1;
    this.failures.set(channel, failures);

    if (failures >= MAX_FAILURES) {
      this.setStatus(channel, 'failed');
      return;
    }

    const attempt = this.attempts.get(channel) ?? 0;
    const wait = Math.min(30_000, 1000 * 2 ** attempt);
    this.attempts.set(channel, attempt + 1);
    this.setStatus(channel, 'closed');

    const prev = this.reconnectTimers.get(channel);
    if (prev) window.clearTimeout(prev);
    const timer = window.setTimeout(() => {
      if (this.listeners.has(channel) && this.listeners.get(channel)!.size > 0) {
        this.connect(channel, path);
      }
    }, wait);
    this.reconnectTimers.set(channel, timer);
  }

  disconnect(channel: string): void {
    const timer = this.reconnectTimers.get(channel);
    if (timer) {
      window.clearTimeout(timer);
      this.reconnectTimers.delete(channel);
    }
    const ws = this.connections.get(channel);
    if (ws) {
      ws.onclose = null;
      ws.onerror = null;
      ws.close();
      this.connections.delete(channel);
    }
    this.listeners.delete(channel);
    this.paths.delete(channel);
    this.attempts.delete(channel);
    this.failures.delete(channel);
    this.channelStatus.delete(channel);
  }

  on(channel: string, handler: WsHandler): () => void {
    if (!this.listeners.has(channel)) {
      this.listeners.set(channel, new Set());
    }
    this.listeners.get(channel)!.add(handler);
    return () => {
      this.listeners.get(channel)?.delete(handler);
    };
  }

  private emit(channel: string, data: Parameters<WsHandler>[0]): void {
    this.listeners.get(channel)?.forEach((fn) => fn(data));
  }
}

export const wsManager = new WebSocketManager();
